import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { authService, Enable2FAResponse } from '../services/authService';
import { getApiError } from '../utils/apiError';

interface TotpFormData {
  totpToken: string;
}

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState<Enable2FAResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'setup' | 'verify'>('setup');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TotpFormData>();

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      const data = await authService.setup2FA();
      setSetup2FAData(data);
      setStep('setup');
      setSetupDialogOpen(true);
    } catch (error: any) {
      toast.error(getApiError(error, 'Failed to setup 2FA'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (data: TotpFormData) => {
    try {
      setLoading(true);
      await authService.enable2FA(data.totpToken);
      toast.success('2FA enabled successfully!');
      await refreshUser();
      setSetupDialogOpen(false);
      setSetup2FAData(null);
      reset();
    } catch (error: any) {
      toast.error(getApiError(error, 'Invalid 2FA token'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (data: TotpFormData) => {
    try {
      setLoading(true);
      await authService.disable2FA(data.totpToken);
      toast.success('2FA disabled successfully!');
      await refreshUser();
      setDisableDialogOpen(false);
      reset();
    } catch (error: any) {
      toast.error(getApiError(error, 'Invalid 2FA token'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSetupDialog = () => {
    setSetupDialogOpen(false);
    setSetup2FAData(null);
    setStep('setup');
    reset();
  };

  const handleCloseDisableDialog = () => {
    setDisableDialogOpen(false);
    reset();
  };

  if (!user) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      <Grid container spacing={3}>
        {/* User Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1">
                  {user.full_name}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {user.email}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Role
                </Typography>
                <Chip
                  label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  color="primary"
                  size="small"
                />
              </Box>
              {user.tenant_id && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Tenant ID
                  </Typography>
                  <Typography variant="body1">
                    {user.tenant_id}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Security Settings
              </Typography>

              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body1">
                    Two-Factor Authentication (2FA)
                  </Typography>
                  <Chip
                    label={user.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                    color={user.is_2fa_enabled ? 'success' : 'default'}
                    icon={user.is_2fa_enabled ? <CheckCircleIcon /> : <CancelIcon />}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Add an extra layer of security to your account by requiring a 2FA token during login.
                </Typography>

                {!user.is_2fa_enabled ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSetup2FA}
                    disabled={loading}
                    startIcon={<SecurityIcon />}
                  >
                    Enable 2FA
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDisableDialogOpen(true)}
                    disabled={loading}
                    startIcon={<CancelIcon />}
                  >
                    Disable 2FA
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Setup 2FA Dialog */}
      <Dialog
        open={setupDialogOpen}
        onClose={handleCloseSetupDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Setup Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          {step === 'setup' && setup2FAData && (
            <>
              <Typography variant="body1" gutterBottom>
                Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.):
              </Typography>

              <Box display="flex" justifyContent="center" my={3}>
                <Box
                  component="img"
                  src={`data:image/png;base64,${setup2FAData.qr_code}`}
                  alt="2FA QR Code"
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                  }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Backup Codes</strong>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {setup2FAData.backup_codes.map((code, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {code}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Alert>

              <Button
                variant="contained"
                onClick={() => setStep('verify')}
                fullWidth
                sx={{ mt: 2 }}
              >
                I've Saved My Backup Codes
              </Button>
            </>
          )}

          {step === 'verify' && (
            <Box component="form" onSubmit={handleSubmit(handleEnable2FA)}>
              <Typography variant="body1" gutterBottom>
                Enter the 6-digit code from your authenticator app to complete setup:
              </Typography>

              <TextField
                {...register('totpToken', {
                  required: '2FA token is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: '2FA token must be 6 digits',
                  },
                })}
                fullWidth
                label="2FA Token"
                type="text"
                inputProps={{ maxLength: 6 }}
                error={!!errors.totpToken}
                helperText={errors.totpToken?.message}
                margin="normal"
                autoFocus
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSetupDialog}>
            Cancel
          </Button>
          {step === 'verify' && (
            <Button
              onClick={handleSubmit(handleEnable2FA)}
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Enable 2FA'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog
        open={disableDialogOpen}
        onClose={handleCloseDisableDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Disable Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Disabling 2FA will make your account less secure. Are you sure you want to continue?
          </Alert>

          <Box component="form" onSubmit={handleSubmit(handleDisable2FA)}>
            <Typography variant="body1" gutterBottom>
              Enter your current 2FA token to disable two-factor authentication:
            </Typography>

            <TextField
              {...register('totpToken', {
                required: '2FA token is required',
                pattern: {
                  value: /^\d{6}$/,
                  message: '2FA token must be 6 digits',
                },
              })}
              fullWidth
              label="2FA Token"
              type="text"
              inputProps={{ maxLength: 6 }}
              error={!!errors.totpToken}
              helperText={errors.totpToken?.message}
              margin="normal"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDisableDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleDisable2FA)}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;